import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Textarea,
  Badge,
  SimpleGrid,
  Container,
  Heading,
  useColorModeValue,
  Spinner,
  Center,
  IconButton,
  Tooltip,
  Alert,
  AlertIcon,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  useToast,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton
} from '@chakra-ui/react';
import { 
  FiTag, 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiTrendingUp,
  FiRefreshCw,
  FiBarChart,
  FiHash
} from 'react-icons/fi';
import { ContentManagementService, type ContentTag } from '../services/contentManagementService';
import { useAuth } from '../contexts/AuthContext';
import IconWrapper from './IconWrapper';

interface TagManagementProps {
  onTagSelect?: (tagId: string) => void;
  showHeader?: boolean;
  allowMultipleSelection?: boolean;
  selectedTags?: string[];
  onTagsChange?: (tags: string[]) => void;
}

const TagManagement: React.FC<TagManagementProps> = ({
  onTagSelect,
  showHeader = true,
  allowMultipleSelection = false,
  selectedTags = [],
  onTagsChange
}) => {
  const { user } = useAuth();
  const toast = useToast();
  const [tags, setTags] = useState<ContentTag[]>([]);
  const [trendingTags, setTrendingTags] = useState<ContentTag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<ContentTag | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Form states
  const [tagName, setTagName] = useState('');
  const [tagDescription, setTagDescription] = useState('');
  const [tagColor, setTagColor] = useState('#6366f1');

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const colors = [
    '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f59e0b',
    '#10b981', '#06b6d4', '#3b82f6', '#84cc16', '#f97316'
  ];

  useEffect(() => {
    loadTags();
    loadTrendingTags();
  }, []);

  const loadTags = async () => {
    try {
      setIsLoading(true);
      const tagsData = await ContentManagementService.getTags();
      setTags(tagsData);
    } catch (error) {
      console.error('Error loading tags:', error);
      toast({
        title: 'Error loading tags',
        description: 'Failed to load tags. Please try again.',
        status: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadTrendingTags = async () => {
    try {
      const trendingData = await ContentManagementService.getTrendingTags();
      setTrendingTags(trendingData);
    } catch (error) {
      console.error('Error loading trending tags:', error);
    }
  };

  const handleCreateTag = async () => {
    if (!tagName.trim()) {
      toast({
        title: 'Tag name required',
        description: 'Please enter a tag name.',
        status: 'error'
      });
      return;
    }

    try {
      setIsCreating(true);
      await ContentManagementService.createTag(tagName, tagDescription, tagColor);
      
      toast({
        title: 'Tag created',
        description: 'Tag has been created successfully.',
        status: 'success'
      });
      
      setTagName('');
      setTagDescription('');
      setTagColor('#6366f1');
      await loadTags();
    } catch (error: any) {
      console.error('Error creating tag:', error);
      toast({
        title: 'Error creating tag',
        description: error.message || 'Failed to create tag.',
        status: 'error'
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditTag = async () => {
    if (!selectedTag) return;

    try {
      await ContentManagementService.updateTagUsage(selectedTag.id, true);
      
      toast({
        title: 'Tag updated',
        description: 'Tag has been updated successfully.',
        status: 'success'
      });
      
      setIsEditModalOpen(false);
      setSelectedTag(null);
      await loadTags();
    } catch (error: any) {
      console.error('Error updating tag:', error);
      toast({
        title: 'Error updating tag',
        description: error.message || 'Failed to update tag.',
        status: 'error'
      });
    }
  };

  const handleTagClick = (tag: ContentTag) => {
    if (allowMultipleSelection) {
      const newSelectedTags = selectedTags.includes(tag.id)
        ? selectedTags.filter(id => id !== tag.id)
        : [...selectedTags, tag.id];
      onTagsChange?.(newSelectedTags);
    } else {
      onTagSelect?.(tag.id);
    }
  };

  const handleTagRemove = (tagId: string) => {
    if (allowMultipleSelection) {
      const newSelectedTags = selectedTags.filter(id => id !== tagId);
      onTagsChange?.(newSelectedTags);
    }
  };

  const filteredTags = tags.filter(tag =>
    tag.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const renderTagCard = (tag: ContentTag) => {
    const isSelected = selectedTags.includes(tag.id);
    
    return (
      <Box
        key={tag.id}
        bg={cardBg}
        borderRadius="lg"
        border="1px"
        borderColor={isSelected ? 'purple.400' : borderColor}
        p={4}
        cursor="pointer"
        transition="all 0.2s"
        _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
        onClick={() => handleTagClick(tag)}
      >
        <VStack align="stretch" gap={3}>
          <HStack justify="space-between">
            <HStack gap={2}>
              <Box
                w={3}
                h={3}
                borderRadius="full"
                bg={tag.color}
              />
              <Text fontWeight="bold" fontSize="md" color="white">
                #{tag.name}
              </Text>
              {tag.isTrending && (
                <Badge colorScheme="red" fontSize="xs">
                  <IconWrapper icon={FiTrendingUp} size={10} style={{ marginRight: '4px' }} />
                  Trending
                </Badge>
              )}
            </HStack>
            
            <HStack gap={2}>
              <Tooltip label="Usage count">
                <Badge colorScheme="purple" fontSize="sm">
                  {formatNumber(tag.usageCount)}
                </Badge>
              </Tooltip>
              
              {user && (
                <HStack gap={1}>
                  <Tooltip label="Edit tag">
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={<IconWrapper icon={FiEdit} size={14} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTag(tag);
                        setIsEditModalOpen(true);
                      }}
                      aria-label="Edit tag"
                    />
                  </Tooltip>
                </HStack>
              )}
            </HStack>
          </HStack>

          {tag.description && (
            <Text color="gray.400" fontSize="sm" noOfLines={2}>
              {tag.description}
            </Text>
          )}
        </VStack>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Center py={8}>
        <Spinner size="lg" color="purple.400" />
      </Center>
    );
  }

  return (
    <Container maxW="container.xl">
      <VStack gap={6} align="stretch">
        {showHeader && (
          <HStack justify="space-between">
            <Heading size="lg" color="white">
              <IconWrapper icon={FiTag} size={24} style={{ marginRight: '8px' }} />
              Tag Management
            </Heading>
            <Tooltip label="Refresh tags">
              <IconButton
                aria-label="Refresh"
                icon={<IconWrapper icon={FiRefreshCw} size={16} />}
                onClick={loadTags}
                variant="ghost"
                colorScheme="purple"
              />
            </Tooltip>
          </HStack>
        )}

        {/* Selected Tags */}
        {allowMultipleSelection && selectedTags.length > 0 && (
          <Box bg={cardBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
            <Text fontWeight="bold" color="white" mb={3}>
              Selected Tags ({selectedTags.length})
            </Text>
            <Wrap gap={2}>
              {selectedTags.map(tagId => {
                const tag = tags.find(t => t.id === tagId);
                if (!tag) return null;
                
                return (
                  <WrapItem key={tagId}>
                    <Tag
                      size="md"
                      colorScheme="purple"
                      borderRadius="full"
                      cursor="pointer"
                      onClick={() => handleTagRemove(tagId)}
                    >
                      <TagLabel>#{tag.name}</TagLabel>
                      <TagCloseButton />
                    </Tag>
                  </WrapItem>
                );
              })}
            </Wrap>
          </Box>
        )}

        {/* Trending Tags */}
        {trendingTags.length > 0 && (
          <Box bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
            <HStack mb={4}>
              <IconWrapper icon={FiTrendingUp} size={20} color="red.400" />
              <Text fontWeight="bold" color="white">
                Trending Tags
              </Text>
            </HStack>
            <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} gap={3}>
              {trendingTags.slice(0, 8).map(renderTagCard)}
            </SimpleGrid>
          </Box>
        )}

        {/* Search and Create */}
        <HStack gap={4}>
          <Input
            placeholder="Search tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg="gray.700"
            border="none"
            color="white"
            _placeholder={{ color: 'gray.400' }}
            _focus={{ bg: 'gray.600' }}
            flex={1}
          />
          {user && (
            <Button
              colorScheme="purple"
              leftIcon={<IconWrapper icon={FiPlus} size={16} />}
              onClick={() => setIsEditModalOpen(true)}
            >
              Create Tag
            </Button>
          )}
        </HStack>

        {/* Tags List */}
        {filteredTags.length === 0 ? (
          <Center py={8}>
            <VStack gap={2}>
              <Text color="gray.500">
                {searchQuery ? 'No tags found matching your search' : 'No tags found'}
              </Text>
              {!searchQuery && (
                <Text fontSize="sm" color="gray.400">
                  Create your first tag to get started
                </Text>
              )}
            </VStack>
          </Center>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} gap={4}>
            {filteredTags.map(renderTagCard)}
          </SimpleGrid>
        )}

        {/* Create/Edit Tag Modal */}
        <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
          <ModalOverlay />
          <ModalContent bg="gray.800" color="white">
            <ModalHeader>
              {selectedTag ? 'Edit Tag' : 'Create New Tag'}
            </ModalHeader>
            <ModalBody>
              <VStack gap={4}>
                <FormControl>
                  <FormLabel>Tag Name</FormLabel>
                  <Input
                    placeholder="Enter tag name"
                    value={tagName}
                    onChange={(e) => setTagName(e.target.value)}
                    bg="gray.700"
                    border="none"
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{ bg: 'gray.600' }}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Description (Optional)</FormLabel>
                  <Textarea
                    placeholder="Enter tag description"
                    value={tagDescription}
                    onChange={(e) => setTagDescription(e.target.value)}
                    bg="gray.700"
                    border="none"
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{ bg: 'gray.600' }}
                    rows={3}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Tag Color</FormLabel>
                  <HStack gap={2} flexWrap="wrap">
                    {colors.map(color => (
                      <Box
                        key={color}
                        w={8}
                        h={8}
                        borderRadius="full"
                        bg={color}
                        cursor="pointer"
                        border={tagColor === color ? '3px solid white' : '1px solid gray'}
                        onClick={() => setTagColor(color)}
                      />
                    ))}
                  </HStack>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={() => setIsEditModalOpen(false)}>
                Cancel
              </Button>
              <Button
                colorScheme="purple"
                onClick={selectedTag ? handleEditTag : handleCreateTag}
                isLoading={isCreating}
                loadingText={selectedTag ? "Updating..." : "Creating..."}
              >
                {selectedTag ? 'Update' : 'Create'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Container>
  );
};

export default TagManagement;
